import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { motion } from "framer-motion";

import {

  Shield,

  ShieldCheck,

  Lock,

  KeyRound,

  Eye,

  ServerCog,

  Globe,

  Fingerprint,

  Cpu,

  RadioTower,

  FileLock2,

  CircuitBoard,

} from "lucide-react";

import { cn } from "../../lib/utils";



const PILLAR_META = [

  { icon: ShieldCheck, accent: "from-[#2568ff] to-[#47b0ff]" },

  { icon: Lock, accent: "from-[#47b0ff] to-[#22D3EE]" },

  { icon: ServerCog, accent: "from-[#22D3EE] to-[#2568ff]" },

  { icon: RadioTower, accent: "from-[#FF7AB6] to-[#2568ff]" },

  { icon: Eye, accent: "from-[#2568ff] to-[#FF7AB6]" },

  { icon: KeyRound, accent: "from-[#FFD66B] to-[#FF7AB6]" },

  { icon: Fingerprint, accent: "from-[#22D3EE] to-[#FFD66B]" },

  { icon: FileLock2, accent: "from-[#2568ff] to-[#22D3EE]" },

  { icon: Globe, accent: "from-[#47b0ff] to-[#FF7AB6]" },

];



function PillarCard({ pillar, index }) {

  const Icon = pillar.icon;

  return (

    <motion.div

      initial={{ opacity: 0, y: 24 }}

      whileInView={{ opacity: 1, y: 0 }}

      viewport={{ once: true, amount: 0.2 }}

      transition={{ duration: 0.6, delay: index * 0.05 }}

      whileHover={{ y: -4 }}

      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md transition-colors hover:border-white/20"

    >

      <div

        aria-hidden

        className={cn(

          "pointer-events-none absolute -inset-px rounded-2xl opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-30",

          "bg-gradient-to-br",

          pillar.accent,

        )}

      />

      <div

        aria-hidden

        className="pointer-events-none absolute inset-0 rounded-2xl opacity-[0.05] [background:radial-gradient(120%_120%_at_0%_0%,white,transparent_60%)]"

      />



      <div className="relative">

        <div

          className={cn(

            "mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10",

            "bg-gradient-to-br",

            pillar.accent,

          )}

        >

          <Icon className="h-5 w-5 text-white" aria-hidden />

        </div>

        <h3 className="text-lg font-semibold text-white">{pillar.title}</h3>

                <p className="mt-2 text-sm leading-relaxed text-white/72">

          {pillar.description}

        </p>

      </div>

    </motion.div>

  );

}



export default function SecuritySection() {

  const { t, i18n } = useTranslation();



  const pillarCopy = useMemo(() => {

    const rows = t("home.security.pillars", { returnObjects: true });

    return Array.isArray(rows) ? rows : [];

  }, [t, i18n.language]);



  const pillars = useMemo(

    () =>

      PILLAR_META.map((meta, i) => ({

        icon: meta.icon,

        accent: meta.accent,

        title: pillarCopy[i]?.title ?? "",

        description: pillarCopy[i]?.description ?? "",

      })),

    [pillarCopy],

  );



  const stats = useMemo(() => {

    const rows = t("home.security.metrics", { returnObjects: true });

    return Array.isArray(rows) ? rows : [];

  }, [t, i18n.language]);



  const standards = useMemo(() => {

    const rows = t("home.security.standards", { returnObjects: true });

    return Array.isArray(rows) ? rows : [];

  }, [t, i18n.language]);



  const titleLines = t("home.security.title").split("\n");



  return (

    <section className="relative isolate w-full overflow-hidden bg-black py-24 md:py-32 text-white">

      <div

        aria-hidden

        className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60%_60%_at_50%_0%,rgba(122,92,255,0.18),transparent_60%),radial-gradient(40%_40%_at_100%_100%,rgba(122,149,181,0.12),transparent_60%)]"

      />

      <div

        aria-hidden

        className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px] [mask-image:radial-gradient(circle_at_center,black,transparent_75%)]"

      />



      <div className="relative mx-auto max-w-7xl px-6">

        <div className="mx-auto max-w-3xl text-center">

          <motion.div

            initial={{ opacity: 0, y: 16 }}

            whileInView={{ opacity: 1, y: 0 }}

            viewport={{ once: true }}

            transition={{ duration: 0.6 }}

            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-medium text-white/70 backdrop-blur"

          >

            <Shield className="h-3.5 w-3.5 text-[#47b0ff]" aria-hidden />

            {t("home.security.heroBadge")}

          </motion.div>



          <motion.h2

            initial={{ opacity: 0, y: 24 }}

            whileInView={{ opacity: 1, y: 0 }}

            viewport={{ once: true }}

            transition={{ duration: 0.7, delay: 0.05 }}

            className="mt-5 text-4xl md:text-6xl font-bold tracking-tight bg-gradient-to-br from-white via-white to-white/60 bg-clip-text text-transparent"

          >

            {titleLines[0]}

            <br className="hidden md:block" />

            {titleLines[1] ?? ""}

          </motion.h2>



          <motion.p

            initial={{ opacity: 0, y: 24 }}

            whileInView={{ opacity: 1, y: 0 }}

            viewport={{ once: true }}

            transition={{ duration: 0.7, delay: 0.1 }}

            className="mt-5 text-base md:text-lg text-white/74"

          >

            {t("home.security.subtitle")}

          </motion.p>

        </div>



        <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-4">

          {stats.map((s, i) => (

            <motion.div

              key={`${s.label}-${i}`}

              initial={{ opacity: 0, y: 16 }}

              whileInView={{ opacity: 1, y: 0 }}

              viewport={{ once: true }}

              transition={{ duration: 0.6, delay: 0.05 * i }}

              className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-center backdrop-blur"

            >

              <div className="text-2xl md:text-3xl font-bold text-white">

                {s.value}

              </div>

              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/62">

                {s.label}

              </div>

            </motion.div>

          ))}

        </div>



        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">

          {pillars.map((p, i) => (

            <PillarCard key={p.title || i} pillar={p} index={i} />

          ))}

        </div>



        <motion.div

          initial={{ opacity: 0, y: 16 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true }}

          transition={{ duration: 0.6, delay: 0.1 }}

          className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur"

        >

          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">

            <div className="flex items-start gap-3">

              <div className="rounded-lg border border-white/10 bg-white/[0.05] p-2">

                <CircuitBoard className="h-5 w-5 text-[#47b0ff]" aria-hidden />

              </div>

              <div>

                <h3 className="text-base font-semibold text-white">

                  {t("home.security.standardsTitle")}

                </h3>

                <p className="mt-1 text-sm text-white/68">

                  {t("home.security.standardsSubtitle")}

                </p>

              </div>

            </div>



            <div className="flex flex-wrap gap-2">

              {standards.map((s) => (

                <span

                  key={s}

                  className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70"

                >

                  {s}

                </span>

              ))}

            </div>

          </div>

        </motion.div>



        <motion.div

          initial={{ opacity: 0, y: 16 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true }}

          transition={{ duration: 0.6, delay: 0.15 }}

          className="mt-10 flex flex-col items-center justify-center gap-3 text-center text-sm text-white/68 md:flex-row"

        >

          <Cpu className="h-4 w-4 text-[#47b0ff]" aria-hidden />

          <span>{t("home.security.footerHint")}</span>

        </motion.div>

      </div>

    </section>

  );

}


